import type { Addresses, FormFields } from 'api/types/addresses.types';
import { AppartmentIcon, HotelIcon, OfficeIcon, VillaIcon } from 'assets/icons';
import { ApartmentAddressTypeIcon, HotelAddressTypeIcon, OfficeAddressTypeIcon, VillaAddressTypeIcon } from 'assets/icons/app';
import i18n from 'i18n/config';
import type React from 'react';

interface AddressType {
  id: Addresses.AddressTypeT;
  Icon: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
}

export const WASHMEN_ADDRESS_TYPES: readonly AddressType[] = [
  {
    id: 'Villa',
    Icon: VillaIcon,
  },
  {
    id: 'Apartment',
    Icon: AppartmentIcon,
  },
  {
    id: 'Office',
    Icon: OfficeIcon,
  },
  {
    id: 'Hotel',
    Icon: HotelIcon,
  },
];

export const WASHMEN_DEFAULT_ADDRESS_TYPES: readonly AddressType[] = [
  {
    id: 'Office',
    Icon: OfficeAddressTypeIcon,
  },
  {
    id: 'Hotel',
    Icon: HotelAddressTypeIcon,
  },
  {
    id: 'Villa',
    Icon: VillaAddressTypeIcon,
  },
  {
    id: 'Apartment',
    Icon: ApartmentAddressTypeIcon,
  },
];

export const ADDRESS_SOURCES = {
  WASHMEN: 'WASHMEN',
  CAREEM: 'CAREEM',
};

export const addressNameRenderer = (address: Addresses.Address) =>
  [address.geofenceLabel, address.street, address.villa, address.addressText, address.number]
    .filter((x) => typeof x === 'string' && x.length > 0)
    .join(`${i18n.t('common.comma')} `);

export const fetchAdressLabels = (addressType: Addresses.AddressTypeT) => {
  const addressTypeT = i18n.t(`common.addressTypes.${addressType}`);
  const fields: FormFields[] = [
    {
      name: 'addressText',
      label: i18n.t('common.addressLabels.building'),
      required: true,
      placeholder: i18n.t('common.addressLabels.buildingPlaceholder', {
        addressType: addressTypeT,
      }),
      minLength: 2,
      autoFocus: true,
    },
    {
      name: 'number',
      label: i18n.t('common.addressLabels.addressNumber', { addressType: addressTypeT }),
      required: true,
      placeholder: i18n.t('common.addressLabels.addressNumberPlaceholder', {
        addressType: addressTypeT,
      }),
      minLength: 2,
    },
    {
      name: 'notes',
      label: i18n.t('common.addressLabels.notes'),
      required: false,
      placeholder: i18n.t('common.addressLabels.notesPlaceholder'),
      minLength: 2,
    },
  ];

  switch (addressType) {
    case 'Villa': {
      fields[0] = {
        name: 'addressText',
        label: i18n.t('common.addressLabels.community'),
        placeholder: i18n.t('common.addressLabels.communityPlaceholder'),
        required: true,
      };
      fields[1] = {
        name: 'villa',
        label: i18n.t('common.addressLabels.villa'),
        placeholder: i18n.t('common.addressLabels.villaPlaceholder'),
        required: true,
      };
      fields.splice(1, 0, {
        name: 'street',
        label: i18n.t('common.addressLabels.street'),
        placeholder: i18n.t('common.addressLabels.streetPlaceholder'),
        required: true,
      });

      break;
    }
    case 'Hotel': {
      fields[1].label = i18n.t('common.addressLabels.roomNumber');
      break;
    }
    default:
      break;
  }

  return fields;
};
